import React from 'react';
import { Box, Typography, alpha, Tooltip } from '@mui/material';
import {
  VerifiedUser as VerifiedIcon,
  FemaleRounded as GenderIcon,
  QueryStats as IndexIcon,
  Gavel as LegalIcon,
  TrendingDown,
  TrendingUp,
  Remove,
} from '@mui/icons-material';

// Real sourced values (IFES / UNDP / V-Dem 2023-2024)
const DEMOCRACY_INDICATORS = [
  {
    id: 'vdem',
    label: 'V-Dem Score',
    value: '0.502',
    subtitle: 'Electoral Democracy',
    detail: 'Varieties of Democracy Electoral Democracy Index (2024)',
    status: 'medium' as const,
    statusLabel: 'Partial Democracy',
    trend: 'down' as const,
    trendLabel: '↓ from 0.528 in 2015',
    icon: <IndexIcon />,
    color: 'warning' as const,
  },
  {
    id: 'hdi',
    label: 'HDI Score',
    value: '0.560',
    subtitle: 'Human Dev. Index',
    detail: 'UNDP Human Development Index (2023)',
    status: 'medium' as const,
    statusLabel: 'Medium Development',
    trend: 'up' as const,
    trendLabel: '↑ from 0.534 in 2015',
    icon: <VerifiedIcon />,
    color: 'info' as const,
  },
  {
    id: 'gender',
    label: 'Women in Senate',
    value: '3.6%',
    subtitle: 'Gender Representation',
    detail: '4 of 109 senators are women (2023 cycle). Global avg: 26%.',
    status: 'low' as const,
    statusLabel: 'Far Below Global Avg',
    trend: 'neutral' as const,
    trendLabel: 'Marginal change since 2019',
    icon: <GenderIcon />,
    color: 'error' as const,
  },
  {
    id: 'cedaw',
    label: 'CEDAW Status',
    value: '1985',
    subtitle: 'Gender Treaty Ratified',
    detail: 'Convention on the Elimination of Discrimination Against Women. Signed: April 23, 1984. Ratified: June 13, 1985.',
    status: 'high' as const,
    statusLabel: 'Signed & Ratified',
    trend: 'neutral' as const,
    trendLabel: 'Full signatory since 1985',
    icon: <LegalIcon />,
    color: 'success' as const,
  },
];

type Status = 'high' | 'medium' | 'low';
type TrendDir = 'up' | 'down' | 'neutral';

const STATUS_COLORS: Record<Status, string> = {
  high: '#79BD79',
  medium: '#F59E0B',
  low: '#C2422B',
};

const TrendIcon: React.FC<{ trend: TrendDir }> = ({ trend }) => {
  if (trend === 'up') return <TrendingUp sx={{ fontSize: '0.85rem' }} />;
  if (trend === 'down') return <TrendingDown sx={{ fontSize: '0.85rem' }} />;
  return <Remove sx={{ fontSize: '0.85rem' }} />;
};

const TREND_COLORS: Record<TrendDir, string> = {
  up: '#79BD79',
  down: '#C2422B',
  neutral: '#7E9F7E',
};

interface IndicatorCardProps {
  label: string;
  value: string;
  subtitle: string;
  detail: string;
  status: Status;
  statusLabel: string;
  trend: TrendDir;
  trendLabel: string;
  icon: React.ReactNode;
}

const IndicatorCard: React.FC<IndicatorCardProps> = ({
  label, value, subtitle, detail, status, statusLabel, trend, trendLabel, icon,
}) => {
  const statusColor = STATUS_COLORS[status];

  return (
    <Tooltip title={detail} arrow placement="top">
      <Box
        sx={{
          flex: 1,
          minWidth: 160,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          cursor: 'default',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            transform: 'translateY(-2px)',
            borderColor: statusColor,
          },
        }}
      >
        {/* Icon + label row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: 1.5,
              bgcolor: alpha(statusColor, 0.12),
              color: statusColor,
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1rem' },
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              fontSize: '0.65rem',
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
        </Box>

        {/* Value */}
        <Typography
          sx={{
            fontSize: '1.75rem',
            fontWeight: 800,
            lineHeight: 1,
            color: 'text.primary',
            letterSpacing: '-0.02em',
          }}
        >
          {value}
        </Typography>

        {/* Subtitle */}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
          {subtitle}
        </Typography>

        {/* Status badge */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 10,
            bgcolor: alpha(statusColor, 0.1),
            border: `1px solid ${alpha(statusColor, 0.25)}`,
            width: 'fit-content',
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: '0.62rem',
              fontWeight: 600,
              color: statusColor,
              letterSpacing: '0.04em',
            }}
          >
            {statusLabel}
          </Typography>
        </Box>

        {/* Trend */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: TREND_COLORS[trend],
          }}
        >
          <TrendIcon trend={trend} />
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 500, color: 'text.secondary' }}>
            {trendLabel}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

const DemocracySnapshot: React.FC = () => (
  <Box sx={{ mb: 4 }}>
    {/* Section header */}
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
      <Typography
        variant="overline"
        sx={{
          fontWeight: 700,
          letterSpacing: '0.1em',
          color: 'text.secondary',
          fontSize: '0.7rem',
        }}
      >
        Democracy Health Indicators
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
        V-Dem · UNDP · IFES · IPU
      </Typography>
    </Box>

    {/* Cards */}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {DEMOCRACY_INDICATORS.map((ind) => (
        <IndicatorCard key={ind.id} {...ind} />
      ))}
    </Box>
  </Box>
);

export default DemocracySnapshot;
